import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Loading, Text, toast } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import {
  ClassroomBooking,
  ClassroomBookingStatusEnum,
  ClassroomBookingStatusPayload,
} from "@models/ISchema";
import { bookingDeleteMutation } from "../../graphql/bookingRemove.mutation";
import Modal from "@components/modal";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import { getClassroomBookingValidationMessage } from "@modules/booking/utils/getClassroomBookingValidationMessage";
import css from "./removeBookingModal.module.scss";

interface RemoveBookingProps {
  campus: string;
  booking?: ClassroomBooking;
  open: boolean;
  onClose: () => void;
}

const RemoveBookingModal: React.FC<RemoveBookingProps> = (props: RemoveBookingProps) => {
  const { open, onClose, campus, booking } = props;
  const { id, origin, scenario, contextUrl } = useBookingContextUrl();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [bookingDelete] = useMutation(bookingDeleteMutation, {});
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const onDeleteBooking = async () => {
    try {
      setLoading(true);
      const variables = {
        scenarioId: parseInt(scenario),
        originId: parseInt(origin),
        input: {
          dryRun: false,
          skipValidations: false,
          bookingIds: [parseInt(id)],
          status: ClassroomBookingStatusEnum.Cancelled,
          comment: ClassroomBookingStatusEnum.Cancelled,
        },
      };
      const { data } = await bookingDelete({ variables });
      const deleteBooking: ClassroomBookingStatusPayload = data.cube.classroomBookingChangeStatus;
      if (!deleteBooking?.commited) {
        toast.error(
          getClassroomBookingValidationMessage(
            t,
            deleteBooking?.validationErrors,
            "booking.booking-details.notifications.booking-delete-error",
          ),
          {
            autoClose: 3000,
          },
        );

        return;
      }

      const pathBookings = `/booking/list/${contextUrl}?campus=${campus}`;

      toast.success(t("booking.booking-details.notifications.booking-deleted"), {
        autoClose: 3000,
      });

      history.replace(pathBookings);
    } catch (error) {
      console.error("[onDeleteBooking]", error);
      toast.error(t("booking.booking-details.notifications.booking-delete-error"), {
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && open && <Loading />}
      <Modal
        className={css.modalBookingDetail}
        typeState="confirm"
        show={open}
        onClose={onClose}
        title={t("booking.booking-details.remove-booking-modal.title")}
        colorButtonPrincipal="danger"
        textButtonPrincipal={t("booking.booking-details.actions.delete-booking")}
        textButtonSecondary={t("booking.booking-details.actions.cancel")}
        isDisabledButtonPrincipal={loading}
        isLoading={loading}
        onClickPrincipal={() => onDeleteBooking()}
        onClickSecondary={onClose}
      >
        <Text>
          {t("booking.booking-details.remove-booking-modal.short-description")}
          {booking?.classroom?.name
            ? ` ${language.includes("es") ? "para" : "to"} "${booking?.classroom?.name}"`
            : ""}
          . {t("booking.booking-details.remove-booking-modal.description")}
        </Text>
      </Modal>
    </>
  );
};

export default RemoveBookingModal;
